import { Injectable, Injector } from '@angular/core';
import { ConnectionPositionPair, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { MenuFilterComponent } from '@shared/components/menu-filter/menu-filter.component';
import { MenuOverlayRef } from './menu-overlay-ref';
import { MENU_DIALOG_DATA, OVERLAY_REF, OVERLAY } from './menu-overlay-data.tokens';

interface DialogConfig {
    panelClass?: string;
    hasBackdrop?: boolean;
    backdropClass?: string;
}

const DEFAULT_CONFIG: DialogConfig = {
    hasBackdrop: true,
    backdropClass: 'dark-backdrop',
    panelClass: 'menu-panel'
};

@Injectable({
    providedIn: 'root'
})
export class MenuFilterService {
    dialogRef: MenuOverlayRef;

    constructor(
        private injector: Injector,
        private overlay: Overlay
    ) {}

    open(config: any = {}): MenuOverlayRef {
        const dialogConfig = {
            ...DEFAULT_CONFIG,
            ...config
        };

        const overlayRef = this.createOverlay(dialogConfig);
        this.dialogRef = new MenuOverlayRef(overlayRef);

        this.attachDialogContainer(overlayRef, dialogConfig, this.dialogRef, this.overlay);

        return this.dialogRef;
    }

    attachDialogContainer(overlayRef: any, config: any, dialogRef: any, overlay: any) {
        const injector = this.createInjector(config, dialogRef, overlayRef, overlay);

        const menuPortal = new ComponentPortal(MenuFilterComponent, null, injector);
        overlayRef.attach(menuPortal);
        return overlayRef.instance;
    }

    createInjector(config: any, dialogRef: any, overlayRef: any, overlay: any) {
        const injectionTokens = new WeakMap();
        injectionTokens.set(MenuOverlayRef, dialogRef);
        injectionTokens.set(MENU_DIALOG_DATA, config);
        injectionTokens.set(OVERLAY_REF, overlayRef);
        injectionTokens.set(OVERLAY, overlay);

        return new PortalInjector(this.injector, injectionTokens);
    }

    private createOverlay(config: DialogConfig) {
        // Returns an OverlayConfig
        const overlayConfig = this.getOverlayConfig(config);
        // Returns an OverlayRef
        return this.overlay.create(overlayConfig);
    }

    private getOverlayConfig(config: any): OverlayConfig {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(config.elemRef)
            .withPositions([
                new ConnectionPositionPair(
                    {
                        originX: 'center',
                        originY: 'bottom'
                    },
                    {
                        overlayX: 'center',
                        overlayY: 'bottom'
                    }
                )
            ]);

        const overlayConfig = new OverlayConfig({
            height: config.height,
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            panelClass: config.panelClass,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            positionStrategy
        });
        return overlayConfig;
    }

    closeOverlay() {
        this.dialogRef && this.dialogRef.close();
    }
}
